/*
  EvidenceDrawer — spec TAF-011 ("Support replay/open-context from
  the relevant transcript evidence").

  Opens as a right-side sheet when a clinician clicks the "Play
  evidence" affordance on any transcript suggestion. Renders:

    · The exact evidence quote extracted by the LLM.
    · The wider transcript-segment context that Whisper produced.
    · An <audio> player pointed at the exact webm segment, seeking
      to the `start_ms` timestamp so play resumes at the moment
      the statement was made.
    · The suggestion's field label + speaker/certainty chips for
      quick orientation.

  Timestamps are RELATIVE to their audio segment. The audio URL
  pattern is:

      /api/soap-notes/sessions/{sessionId}/segments/{audioIndex}.webm

  which requires an authenticated cookie (already set for the
  logged-in clinician). The drawer degrades gracefully when the
  suggestion has no audio locator (older sessions extracted before
  Whisper verbose_json was enabled) — it still shows the quote and
  matched context.
*/
import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Mic, Play, Pause, AlertTriangle } from "lucide-react";

function fmtTime(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "0:00";
  const totalSec = Math.floor(ms / 1000);
  const mm = Math.floor(totalSec / 60);
  const ss = totalSec % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
}

/**
 * Props:
 *   open        — sheet visibility.
 *   suggestion  — full suggestion row with .evidence, .assertion,
 *                 .soap_session_id, .field_label, .proposed_value.
 *   onClose()   — dismiss callback.
 */
export default function EvidenceDrawer({ open, suggestion, onClose }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const evidence = suggestion?.evidence || {};
  const sessionId = suggestion?.soap_session_id;
  const audioIndex = evidence.audio_index;
  const startMs = evidence.start_ms;
  const endMs = evidence.end_ms;
  const hasAudio = sessionId && Number.isFinite(audioIndex) && Number.isFinite(startMs);
  const audioUrl = hasAudio
    ? `${API}/soap-notes/sessions/${sessionId}/segments/${audioIndex}.webm`
    : null;

  useEffect(() => {
    // When the sheet opens (or the selected suggestion changes), seek
    // the audio element to the evidence start time. `loadedmetadata`
    // isn't always fired quickly enough, so we set currentTime once
    // the browser reports it can seek.
    if (!open || !hasAudio) return;
    const el = audioRef.current;
    if (!el) return;
    setPlaying(false);
    const seek = () => {
      try {
        el.currentTime = (startMs || 0) / 1000;
      } catch { /* not seekable yet */ }
    };
    el.addEventListener("loadedmetadata", seek, { once: true });
    // Best-effort immediate seek if metadata already loaded.
    if (el.readyState >= 1) seek();
    return () => el.removeEventListener("loadedmetadata", seek);
  }, [open, hasAudio, startMs, audioUrl]);

  function togglePlay() {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play(); setPlaying(true);
    } else {
      el.pause(); setPlaying(false);
    }
  }

  const assertion = suggestion?.assertion || {};

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col p-0"
        data-testid="evidence-drawer"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-zinc-200 shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-violet-600" />
            Transcript evidence
          </SheetTitle>
          {suggestion && (
            <div className="text-sm text-zinc-600">
              Field: <b>{suggestion.field_label || suggestion.field_key}</b>
            </div>
          )}
        </SheetHeader>

        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0 space-y-4">
          {/* Proposed value */}
          <div className="rounded border border-violet-200 bg-violet-50 p-3">
            <div className="text-2xs uppercase tracking-wider text-violet-800 font-semibold">
              AI-suggested value
            </div>
            <div className="text-sm mt-1 whitespace-pre-wrap">
              {String(suggestion?.proposed_value ?? "")}
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {assertion.speaker && (
                <Chip label={`speaker: ${assertion.speaker}`} />
              )}
              {assertion.experiencer && assertion.experiencer !== "self" && (
                <Chip label={`about: ${String(assertion.experiencer).replace("_", " ")}`} tone="rose" />
              )}
              {assertion.laterality && assertion.laterality !== "unspecified" && (
                <Chip label={`side: ${assertion.laterality}`} />
              )}
              {assertion.units && (
                <Chip label={`units: ${assertion.units}`} />
              )}
              {assertion.certainty && assertion.certainty !== "stated" && (
                <Chip label={`certainty: ${assertion.certainty}`} />
              )}
              {assertion.negation && <Chip label="negated" tone="rose" />}
              {typeof suggestion?.model_metadata?.confidence_score === "number" && (
                <Chip label={`${Math.round(suggestion.model_metadata.confidence_score * 100)}% confident`} />
              )}
            </div>
          </div>

          {/* Evidence quote */}
          <div>
            <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">
              Extracted quote
            </div>
            <div className="rounded border-l-2 border-violet-400 bg-white pl-3 py-2 text-sm italic text-zinc-800">
              {evidence.text ? `“${evidence.text}”` : <span className="not-italic text-zinc-500">— no quote captured —</span>}
            </div>
          </div>

          {/* Broader matched context */}
          {evidence.matched_text && evidence.matched_text !== evidence.text && (
            <div>
              <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                Matched transcript segment
              </div>
              <div className="rounded bg-zinc-50 border border-zinc-200 p-3 text-sm text-zinc-700 whitespace-pre-wrap">
                {evidence.matched_text}
              </div>
            </div>
          )}

          {/* Audio player */}
          <div>
            <div className="text-2xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">
              Audio playback
            </div>
            {hasAudio ? (
              <div className="rounded border border-zinc-200 bg-white p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="h-9 w-9 rounded-full border border-violet-400 bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700"
                    data-testid="evidence-drawer-play-toggle"
                  >
                    {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                  </button>
                  <div className="text-xs text-zinc-600 font-mono-num">
                    seek: <b>{fmtTime(startMs)}</b>
                    {Number.isFinite(endMs) && endMs > startMs && (
                      <> → {fmtTime(endMs)}</>
                    )}
                    <span className="text-zinc-400"> · segment {audioIndex}</span>
                  </div>
                </div>
                <audio
                  ref={audioRef}
                  controls
                  preload="metadata"
                  src={audioUrl}
                  className="w-full"
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  data-testid="evidence-drawer-audio"
                >
                  Your browser does not support the audio element.
                </audio>
                <div className="text-2xs text-zinc-500">
                  Uses your existing login — no additional access needed.
                </div>
              </div>
            ) : (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <div>
                  No audio locator was captured for this suggestion. Older
                  sessions transcribed before timestamped Whisper output
                  won't have playable evidence — the extracted quote above
                  is the full context.
                </div>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Chip({ label, tone = "zinc" }) {
  const cls = tone === "rose"
    ? "border-rose-300 bg-rose-100 text-rose-900"
    : "border-zinc-300 bg-white text-zinc-700";
  return (
    <span className={`text-2xs px-1.5 py-0.5 rounded border ${cls}`}>
      {label}
    </span>
  );
}
